import {useMemo} from 'react';

import type {Row, SortBy, VirtualizeProps} from 'shells/table/types';

import {ErrorState} from '../error-state/error-state';
import {LoadingPage} from '../loading-page/loading-page';
import {FormattedTimestamp} from '../timestamp/formatted-timestamp';
import {RelativeTimestamp} from '../timestamp/relative-timestamp';

import {LegacySetupTableContainer} from './legacy-setup-table-container';
import {Table} from './table';
import {AddButtonRow} from './table-body/add-button-row';

import styles from './setup-item-table.module.css';

export type Item = {
    id: string;
    name: string;
    type?: string | null | undefined;
    createdTime: any | null | undefined;
    isDeleteable?: boolean;
    isEditable?: boolean;
    creator?: any;
};

export type AdditionalColumn = {
    index: number;
    column: any;
};

type Props = {
    items: Item[];
    title: string;
    error: string | null | undefined;
    isLoading: boolean;
    canLoadMore?: boolean;
    emptyState: React.ReactNode;
    onAddRowClick: () => void | null | undefined;
    addButtonText?: string;
    onSelectRow?: (row: Row<any>) => void;
    additionalColumns?: AdditionalColumn[];
    sortBy?: SortBy[];
    onChangeSortBy?: (sortBy: SortBy[]) => void;
    virtualizeProps?: VirtualizeProps;
    renderRowActionMenu?: (
        row: Row<any>,
        rowElement?: HTMLDivElement | null | undefined
    ) => React.ReactElement;
    getAddButtonRef?: (addButtonRef?: HTMLDivElement | null | undefined) => void;
    getRowRef?: (rowRef?: HTMLDivElement | null | undefined) => void;
    // Optionally renders the primary row with a subtitle using the field passed in
    primaryColSubtitleField?: string;
    // Whether to include the last used time column; if not passed in, will include by default
    includeLastUsedTimeCol?: boolean;
    includeCreatedTimeCol?: boolean;
    primaryColTertiaryField?: string;
    primaryColSubtitleDefault?: string;
    primaryColTertiaryDefault?: string;
};

export function SetupItemTable(props: Props) {
    const {
        title,
        onAddRowClick,
        primaryColSubtitleField,
        primaryColTertiaryField,
        primaryColSubtitleDefault,
        primaryColTertiaryDefault,
        includeLastUsedTimeCol = true,
        includeCreatedTimeCol = true,
        additionalColumns = [],
    } = props;

    const primaryCol = useMemo(
        () =>
            primaryColSubtitleField
                ? {
                      id: title.toLowerCase(),
                      Header: title,
                      // @ts-expect-error
                      accessor: (row) => {
                          const primaryText = row.name;
                          const subtitleText = row[primaryColSubtitleField];
                          // @ts-expect-error
                          const tertiaryText = row[primaryColTertiaryField];

                          const result = {
                              text: primaryText,
                              subtitle: subtitleText,
                              tertiary: tertiaryText,
                              subtitleDefault: primaryColSubtitleDefault,
                              tertiaryDefault: primaryColTertiaryDefault,
                              subtitleIsPill: row.subtitleIsPill,
                          } as const;

                          return result;
                      },
                      dataType: 'primaryWithSubtitle',
                      width: 150,
                      // @ts-expect-error
                      sortType: (rowA, rowB) => {
                          const nameA = rowA.original.name ? rowA.original.name.toLowerCase() : '';
                          const nameB = rowB.original.name ? rowB.original.name.toLowerCase() : '';

                          return nameA.localeCompare(nameB);
                      },
                  }
                : {
                      id: title.toLowerCase(),
                      Header: title,
                      // @ts-expect-error
                      accessor: (row) => row.name,
                      dataType: 'string',
                      width: 150,
                      shouldTruncate: true,
                  },
        [
            title,
            primaryColSubtitleField,
            primaryColTertiaryField,
            primaryColSubtitleDefault,
            primaryColTertiaryDefault,
        ]
    );

    const columns = useMemo(() => {
        const cols = [
            primaryCol,
            ...(includeLastUsedTimeCol
                ? [
                      {
                          id: 'lastUsedTime',
                          Header: 'Last used',
                          // @ts-expect-error
                          accessor: (row) => row.lastUsedTime,
                          dataType: 'timestamp',
                          minWidth: 150,
                          maxWidth: 150,
                          width: 150,
                          Cell: ({cell}: any) => <RelativeTimestamp datetime={cell.value} />,
                      },
                  ]
                : []),
            ...(includeCreatedTimeCol
                ? [
                      {
                          id: 'createdTime',
                          Header: 'Created time',
                          // @ts-expect-error
                          accessor: (row) => row.createdTime,
                          dataType: 'timestamp',
                          width: 180,
                          Cell: ({cell}: any) => (
                              <FormattedTimestamp
                                  datetime={cell.value}
                                  format={FormattedTimestamp.formats.monthDayYear}
                                  showDetailedPopover={true}
                              />
                          ),
                      },
                  ]
                : []),
        ];

        additionalColumns.forEach((col) => cols.splice(col.index, 0, col.column));

        return cols;
    }, [includeLastUsedTimeCol, primaryCol, includeCreatedTimeCol, additionalColumns]);

    const firstRow = onAddRowClick ? (
        <AddButtonRow
            buttonText={props.addButtonText ? props.addButtonText : `Add ${title.toLowerCase()}`}
            onClick={onAddRowClick}
            getRef={props.getAddButtonRef}
        />
    ) : undefined;

    return (
        // This div allows us to target SetupItemTable and hide the createdTime
        // column at smaller screen widths
        <div className={styles['setup-item-table-container']}>
            <LegacySetupTableContainer overflow='scroll' canLoadMore={props.canLoadMore}>
                <Table
                    firstRow={firstRow}
                    isStriped={true}
                    isResizable={true}
                    hasHeaderBorder={true}
                    hasHeaderCellBorders={true}
                    headerBackgroundColor='offWhite'
                    isCompact={true}
                    stickyHeaders={false}
                    hasBorders={true}
                    hasCellBorders={false}
                    isCheckboxSelectable={false}
                    fullWidth={true}
                    onSelectRow={props.onSelectRow}
                    sortBy={props.sortBy}
                    onChangeSortBy={props.onChangeSortBy}
                    disableFilters={true}
                    shouldShowFooter={false}
                    // @ts-expect-error
                    columns={columns}
                    data={props.items}
                    virtualizeProps={props.virtualizeProps}
                    renderRowActionMenu={props.renderRowActionMenu}
                    getRowRef={props.getRowRef}
                    specialStates={[
                        {
                            shouldRender: () => Boolean(props.error),
                            component: <ErrorState errorMessage={props.error} />,
                        },
                        {
                            shouldRender: () => props.isLoading,
                            component: <LoadingPage padding={32} />,
                        },
                        {
                            shouldRender: () =>
                                !props.error && !props.isLoading && !props.items.length,
                            component: props.emptyState,
                        },
                    ]}
                />
            </LegacySetupTableContainer>
        </div>
    );
}
